<template>
  <div class="maker-studio-view">
    <!-- Header Banner with Systematic Spacing & No Text Overlap -->
    <div class="card mb-4 border-0 shadow-lg text-white header-studio-card" style="background: linear-gradient(135deg, #13192b 0%, #0a0e1a 100%); border-left: 4px solid #10b981 !important; border-radius: 14px;">
      <div class="card-body p-3 p-md-4">
        <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
          <div class="header-content-col" style="max-width: 820px;">
            <div class="d-flex flex-wrap align-items-center mb-2" style="gap: 8px;">
              <span class="badge badge-success text-dark font-weight-bold px-2 py-1">
                <i class="mdi mdi-hammer-wrench mr-1"></i> {{ $t('studio.badgeMaker') }}
              </span>
              <span class="badge badge-outline-warning px-2 py-1">
                <i class="mdi mdi-shield-search mr-1"></i> {{ $t('studio.badgeAi') }}
              </span>
              <span class="badge badge-outline-info px-2 py-1">
                <i class="mdi mdi-creation mr-1"></i> {{ $t('studio.badgeSop') }}
              </span>
            </div>
            <h2 class="font-weight-bold mb-2 text-white" style="letter-spacing: -0.5px;">
              {{ $t('studio.title') }}
            </h2>
            <p class="text-muted mb-0" style="font-size: 14px; line-height: 1.6; color: #94a3b8 !important;">
              {{ $t('studio.subtitle') }}
            </p>
          </div>

          <div class="header-actions-col flex-shrink-0">
            <div class="d-flex flex-wrap align-items-center gap-2">
              <button class="btn btn-success text-dark font-weight-bold shadow-sm" @click="activeTab = 'architect'">
                <i class="mdi mdi-plus-circle mr-1"></i> {{ $t('studio.newQuestBtn') }}
              </button>
              <router-link to="/game-settings" class="btn btn-outline-info font-weight-bold">
                <i class="mdi mdi-cogs mr-1"></i> {{ $t('studio.gameSettingsBtn') }}
              </router-link>
              <router-link to="/realms/marketplace" class="btn btn-outline-warning font-weight-bold">
                <i class="mdi mdi-earth mr-1"></i> {{ $t('studio.marketplaceBtn') }}
              </router-link>
            </div>
          </div>
        </div>

        <!-- 4-Pillar Studio Navigation Tabs -->
        <div class="d-flex mt-4 border-top border-secondary pt-3 flex-wrap" style="gap: 10px;">
          <button
            type="button"
            class="btn font-weight-bold px-3 py-2 text-sm"
            :class="activeTab === 'architect' ? 'btn-success text-dark shadow-sm' : 'btn-outline-secondary text-white'"
            @click="activeTab = 'architect'"
          >
            {{ $t('studio.tabArchitect') }}
          </button>

          <button
            type="button"
            class="btn font-weight-bold px-3 py-2 text-sm"
            :class="activeTab === 'simulator' ? 'btn-success text-dark shadow-sm' : 'btn-outline-secondary text-white'"
            @click="activeTab = 'simulator'"
          >
            {{ $t('studio.tabSimulator') }}
          </button>

          <button
            type="button"
            class="btn font-weight-bold px-3 py-2 text-sm"
            :class="activeTab === 'economy' ? 'btn-success text-dark shadow-sm' : 'btn-outline-secondary text-white'"
            @click="activeTab = 'economy'"
          >
            {{ $t('studio.tabEconomy') }}
          </button>

          <button
            type="button"
            class="btn font-weight-bold px-3 py-2 text-sm position-relative"
            :class="activeTab === 'anticheat' ? 'btn-success text-dark shadow-sm' : 'btn-outline-secondary text-white'"
            @click="activeTab = 'anticheat'"
          >
            {{ $t('studio.tabAntiCheat') }}
            <span class="badge badge-dark text-warning ml-1" v-if="pendingVerifications.length > 0">
              {{ pendingVerifications.length }}
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- TAB 1: AI SOP-TO-QUEST ARCHITECT -->
    <div v-if="activeTab === 'architect'">
      <div class="row">
        <!-- Form Architect Left Column -->
        <div class="col-xl-7 grid-margin stretch-card">
          <div class="card bg-dark border-secondary w-100">
            <div class="card-body p-3 p-md-4">
              <div class="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <h4 class="card-title text-white mb-1">
                    <i class="mdi mdi-robot text-success mr-1"></i> {{ $t('studio.architectTitle') }}
                  </h4>
                  <p class="text-muted small mb-0">
                    {{ $t('studio.architectDesc') }}
                  </p>
                </div>
              </div>

              <!-- AI Prompt Generator Box -->
              <div class="p-3 rounded mb-4" style="background: rgba(16, 185, 129, 0.08); border: 1px dashed rgba(16, 185, 129, 0.3);">
                <label for="aiGoalPromptInput" class="text-white small font-weight-bold d-flex align-items-center mb-1">
                  <i class="mdi mdi-sparkles text-warning mr-1" aria-hidden="true"></i> {{ $t('studio.targetGoalLabel') }}
                </label>
                <textarea
                  id="aiGoalPromptInput"
                  class="form-control bg-dark border-secondary text-white mb-2"
                  rows="2"
                  :placeholder="$t('studio.targetGoalPlaceholder')"
                  v-model="aiGoalPrompt"
                  @input="hasEditedQuest = true"
                ></textarea>
                <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <small class="text-muted">
                    {{ $t('studio.aiHelperText') }}
                  </small>
                  <button
                    type="button"
                    class="btn btn-sm btn-success text-dark font-weight-bold"
                    @click="generateFormulaWithAi"
                    :disabled="isGeneratingAi"
                  >
                    <span v-if="isGeneratingAi" class="spinner-border spinner-border-sm mr-1" aria-hidden="true"></span>
                    <i v-else class="mdi mdi-creation mr-1" aria-hidden="true"></i>
                    {{ $t('studio.generateAiBtn') }}
                  </button>
                </div>
              </div>

              <!-- Quest Specification Form -->
              <form @submit.prevent="deployQuestToRealm">
                <div class="form-group mb-3">
                  <label for="questTitleInput" class="text-white small font-weight-bold">{{ $t('studio.questTitleLabel') }}</label>
                  <input
                    id="questTitleInput"
                    type="text"
                    class="form-control bg-dark border-secondary text-white"
                    :placeholder="$t('studio.questTitlePlaceholder')"
                    v-model="activeQuest.name"
                    @input="hasEditedQuest = true"
                    required
                  />
                </div>

                <div class="row mb-3">
                  <div class="col-md-6 mb-3 mb-md-0">
                    <label for="questDivisionSelect" class="text-white small font-weight-bold">{{ $t('studio.divisionLabel') }}</label>
                    <select id="questDivisionSelect" class="form-control bg-dark border-secondary text-white" v-model="activeQuest.division" @change="hasEditedQuest = true; syncSelectedDivisionId()">
                      <option v-for="divName in divisionOptions" :key="divName" :value="divName">
                        {{ divName }}
                      </option>
                    </select>
                  </div>
                  <div class="col-md-6">
                    <label for="questCadenceSelect" class="text-white small font-weight-bold">{{ $t('studio.cadenceLabel') }}</label>
                    <select id="questCadenceSelect" class="form-control bg-dark border-secondary text-white" v-model="activeQuest.frequency" @change="hasEditedQuest = true">
                      <option value="daily">{{ $t('studio.cadenceDaily') }}</option>
                      <option value="weekly">{{ $t('studio.cadenceWeekly') }}</option>
                      <option value="quarterly">{{ $t('studio.cadenceQuarterly') }}</option>
                    </select>
                  </div>
                </div>

                <!-- Rewards XP & Gold with Transparent Rupiah Conversion -->
                <div class="row mb-3">
                  <div class="col-md-6 mb-3 mb-md-0">
                    <label for="questXpInput" class="text-white small font-weight-bold">{{ $t('studio.rewardXpLabel') }}</label>
                    <input
                      id="questXpInput"
                      type="number"
                      class="form-control bg-dark border-secondary text-white"
                      v-model.number="activeQuest.xp"
                      @input="hasEditedQuest = true"
                      required
                    />
                    <small class="text-muted">{{ $t('studio.xpHelperText') }}</small>
                  </div>
                  <div class="col-md-6">
                    <label for="questGoldInput" class="text-white small font-weight-bold">{{ $t('studio.rewardGoldLabel') }}</label>
                    <input
                      id="questGoldInput"
                      type="number"
                      class="form-control bg-dark border-secondary text-white"
                      v-model.number="activeQuest.gold"
                      @input="hasEditedQuest = true"
                      required
                    />
                    <small class="text-warning font-weight-bold">
                      {{ $t('studio.cashEquivalent') }} Rp {{ formatNumber(activeQuest.gold * 1000) }}
                    </small>
                  </div>
                </div>

                <!-- Self-Checking QA Checklist -->
                <div class="form-group mb-3">
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <label class="text-white small font-weight-bold mb-0">
                      <i class="mdi mdi-checkbox-marked-circle-outline text-success mr-1" aria-hidden="true"></i>
                      {{ $t('studio.checklistTitle') }}
                    </label>
                    <button type="button" class="btn btn-xs btn-outline-info py-0 px-2" @click="addChecklistItem">
                      {{ $t('studio.addChecklistBtn') }}
                    </button>
                  </div>
                  <p class="text-muted small mb-2">{{ $t('studio.checklistDesc') }}</p>

                  <div
                    v-for="(item, idx) in activeQuest.checklist"
                    :key="idx"
                    class="d-flex align-items-center mb-2 gap-2"
                  >
                    <input
                      type="text"
                      class="form-control bg-dark border-secondary text-white form-control-sm"
                      v-model="activeQuest.checklist[idx]"
                      @input="hasEditedQuest = true"
                      :aria-label="$t('studio.checklistTitle') + ' ' + (idx + 1)"
                      :placeholder="$t('studio.checklistItemPlaceholder')"
                    />
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-danger p-1"
                      :aria-label="$t('common.delete')"
                      @click="removeChecklistItem(idx)"
                      v-if="activeQuest.checklist.length > 1"
                    >
                      <i class="mdi mdi-close" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>

                <!-- Evidence Verification Standard -->
                <div class="form-group mb-3">
                  <label for="questEvidenceTextarea" class="text-white small font-weight-bold">{{ $t('studio.evidenceStandardLabel') }}</label>
                  <textarea
                    id="questEvidenceTextarea"
                    class="form-control bg-dark border-secondary text-white"
                    rows="2"
                    :placeholder="$t('studio.evidenceStandardPlaceholder')"
                    v-model="activeQuest.evidenceGuide"
                    @input="hasEditedQuest = true"
                  ></textarea>
                </div>

                <!-- Missed Deadline Penalty -->
                <div class="p-3 rounded mb-4" style="background: rgba(239, 68, 68, 0.08); border: 1px dashed rgba(239, 68, 68, 0.3);">
                  <div class="custom-control custom-checkbox">
                    <input type="checkbox" class="custom-control-input" id="hasConsequenceCheck" v-model="activeQuest.hasConsequence">
                    <label class="custom-control-label text-white small" for="hasConsequenceCheck">
                      <strong>{{ $t('studio.consequenceLabel') }}:</strong> {{ $t('studio.consequenceDesc') }}
                    </label>
                  </div>
                </div>

                <button type="submit" class="btn btn-success text-dark font-weight-bold w-100 py-2 shadow-sm" :disabled="isDeploying">
                  <span v-if="isDeploying" class="spinner-border spinner-border-sm mr-1" aria-hidden="true"></span>
                  <i v-else class="mdi mdi-rocket-launch mr-1" aria-hidden="true"></i>
                  {{ $t('studio.deployQuestBtn') }}
                </button>
              </form>
            </div>
          </div>
        </div>

        <!-- Presets & Quick Testing Right Column -->
        <div class="col-xl-5 grid-margin stretch-card">
          <div class="card bg-dark border-secondary w-100">
            <div class="card-body p-3 p-md-4">
              <h4 class="card-title text-white mb-1">
                <i class="mdi mdi-bookmark-check text-warning mr-1" aria-hidden="true"></i> {{ $t('studio.presetsTitle') }}
              </h4>
              <p class="text-muted small mb-3">{{ $t('studio.presetsSub') }}</p>

              <div class="d-flex flex-column gap-2">
                <div
                  v-for="(p, pIdx) in localizedPresets"
                  :key="pIdx"
                  class="p-3 rounded border border-secondary"
                  style="background: rgba(255, 255, 255, 0.02); transition: border-color 0.2s;"
                >
                  <div class="d-flex justify-content-between align-items-start mb-2">
                    <span class="badge badge-outline-info font-weight-bold text-xs">{{ p.division }}</span>
                    <span class="badge badge-dark text-warning font-weight-bold">
                      <i class="mdi mdi-circle-multiple mr-1" aria-hidden="true"></i>+{{ p.gold }} /
                      <i class="mdi mdi-lightning-bolt mr-1" aria-hidden="true"></i>+{{ p.xp }} XP
                    </span>
                  </div>
                  <h6 class="text-white font-weight-bold mb-1">{{ p.name }}</h6>
                  <p class="text-muted small mb-2" style="font-size: 12px; line-height: 1.5;">{{ p.evidenceGuide }}</p>
                  <button class="btn btn-outline-light btn-sm w-100 font-weight-bold" @click="applyPreset(p)">
                    <i class="mdi mdi-check-circle-outline mr-1" aria-hidden="true"></i> {{ $t('studio.usePreset') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- TAB 2: LIVE MOBILE PLAYER HUD SIMULATOR -->
    <div v-if="activeTab === 'simulator'">
      <div class="row justify-content-center">
        <div class="col-lg-7 grid-margin stretch-card">
          <div class="card bg-dark border-secondary w-100">
            <div class="card-body p-3 p-md-4">
              <h4 class="card-title text-white mb-1">
                <i class="mdi mdi-cellphone-sound text-info mr-1" aria-hidden="true"></i> {{ $t('studio.simulatorTitle') }}
              </h4>
              <p class="text-muted small mb-4">{{ $t('studio.simulatorDesc') }}</p>

              <!-- Phone Device Mockup Container -->
              <div class="phone-frame-mockup mx-auto">
                <!-- Phone Top Speaker & Camera -->
                <div class="phone-notch">
                  <div class="notch-speaker"></div>
                  <div class="notch-lens"></div>
                </div>

                <!-- Phone Inner Screen -->
                <div class="phone-screen-content">
                  <!-- Header Status -->
                  <div class="p-2 d-flex justify-content-between align-items-center border-bottom border-secondary" style="background: #0b0f19;">
                    <div class="d-flex align-items-center gap-1">
                      <span class="text-warning font-weight-bold" style="font-size: 12px;">
                        <i class="mdi mdi-shield-star mr-1" aria-hidden="true"></i>GAMITEAM
                      </span>
                    </div>
                    <span class="badge badge-success text-dark font-weight-bold" style="font-size: 9px;">● {{ $t('studio.simulatorOnline') }}</span>
                  </div>

                  <!-- Simulated Quest Card -->
                  <div class="p-3">
                    <div class="text-muted small text-uppercase font-weight-bold mb-2" style="font-size: 10px;">
                      {{ activeQuest.division }}
                    </div>

                    <div class="sim-quest-card p-3 rounded" style="background: linear-gradient(135deg, rgba(30, 41, 59, 0.95), rgba(15, 23, 42, 0.98)); border: 1px solid rgba(255,255,255,0.12);">
                      <div class="d-flex justify-content-between align-items-start mb-2">
                        <span class="badge badge-success font-weight-bold" style="font-size: 9px;">
                          {{ $t('studio.activeMissionBadge') }}
                        </span>
                        <span class="badge badge-outline-warning font-weight-bold" style="font-size: 10px;">
                          <i class="mdi mdi-circle-multiple mr-1" aria-hidden="true"></i>+{{ activeQuest.gold }} {{ $t('common.gold') }}
                        </span>
                      </div>

                      <h6 class="text-white font-weight-bold mb-2" style="font-size: 14px;">
                        {{ activeQuest.name || $t('studio.sampleQuestTitle') }}
                      </h6>

                      <div class="text-muted mb-3" style="font-size: 11px; line-height: 1.5;">
                        <i class="mdi mdi-information-outline text-info mr-1" aria-hidden="true"></i>
                        {{ activeQuest.evidenceGuide || $t('studio.sampleEvidenceGuide') }}
                      </div>

                      <!-- Interactive Simulated Checklist -->
                      <div class="mb-3 p-2 rounded" style="background: rgba(0,0,0,0.3); border: 1px solid rgba(255,255,255,0.06);">
                        <div class="text-white font-weight-bold mb-1" style="font-size: 11px;">
                          {{ $t('studio.simulatorChecklistLabel') }}
                        </div>
                        <div
                          v-for="(chk, cIdx) in activeQuest.checklist"
                          :key="cIdx"
                          class="custom-control custom-checkbox mb-1"
                        >
                          <input type="checkbox" class="custom-control-input" :id="'simChk' + cIdx" checked />
                          <label class="custom-control-label text-white" :for="'simChk' + cIdx" style="font-size: 11px;">
                            {{ chk }}
                          </label>
                        </div>
                      </div>

                      <!-- Simulated Submit Button -->
                      <button type="button" class="btn btn-warning btn-block font-weight-bold text-dark btn-sm py-2 shadow">
                        <i class="mdi mdi-camera mr-1" aria-hidden="true"></i> {{ $t('studio.tapToSubmit') }} (+{{ activeQuest.xp }} XP)
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- TAB 3: INCENTIVE ECONOMY & BUDGET GUARD -->
    <div v-if="activeTab === 'economy'">
      <div class="row">
        <div class="col-lg-6 grid-margin stretch-card">
          <div class="card bg-dark border-secondary w-100">
            <div class="card-body p-3 p-md-4">
              <h4 class="card-title text-white mb-1">
                <i class="mdi mdi-calculator-variant text-warning mr-1" aria-hidden="true"></i> {{ $t('studio.economyTitle') }}
              </h4>
              <p class="text-muted small mb-4">{{ $t('studio.economyDesc') }}</p>

              <div class="form-group mb-4">
                <label for="simTeamSizeRange" class="text-white small font-weight-bold d-flex justify-content-between">
                  <span>{{ $t('studio.simTeamSizeLabel') }}</span>
                  <span class="text-info font-weight-bold">{{ simTeamSize }} {{ $t('studio.membersUnit') }}</span>
                </label>
                <input id="simTeamSizeRange" type="range" class="form-range w-100" min="5" max="100" step="5" v-model.number="simTeamSize" />
              </div>

              <div class="form-group mb-4">
                <label for="simCompletionRateRange" class="text-white small font-weight-bold d-flex justify-content-between">
                  <span>{{ $t('studio.simCompletionRateLabel') }}</span>
                  <span class="text-success font-weight-bold">{{ simCompletionRate }}%</span>
                </label>
                <input id="simCompletionRateRange" type="range" class="form-range w-100" min="30" max="100" step="5" v-model.number="simCompletionRate" />
              </div>

              <div class="form-group mb-3">
                <label for="goldConversionRateInput" class="text-white small font-weight-bold d-flex justify-content-between">
                  <span>{{ $t('studio.goldConversionLabel') }}</span>
                  <span class="text-warning font-weight-bold">Rp {{ formatNumber(1000) }}</span>
                </label>
                <input id="goldConversionRateInput" type="number" class="form-control bg-dark border-secondary text-white" value="1000" disabled />
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-6 grid-margin stretch-card">
          <div class="card bg-dark border-secondary w-100">
            <div class="card-body p-3 p-md-4 d-flex flex-column justify-content-between">
              <div>
                <h5 class="text-white font-weight-bold mb-3">{{ $t('studio.monthlyProjectionTitle') }}</h5>

                <div class="row g-2 mb-3">
                  <div class="col-6">
                    <div class="p-3 rounded border border-secondary" style="background: rgba(255,255,255,0.03);">
                      <small class="text-muted d-block mb-1">{{ $t('studio.estMonthlyGold') }}</small>
                      <h4 class="text-warning font-weight-bold mb-0">
                        <i class="mdi mdi-circle-multiple mr-1" aria-hidden="true"></i>{{ formatNumber(projectedMonthlyGold) }}
                      </h4>
                    </div>
                  </div>
                  <div class="col-6">
                    <div class="p-3 rounded border border-secondary" style="background: rgba(255,255,255,0.03);">
                      <small class="text-muted d-block mb-1">{{ $t('studio.estLiability') }}</small>
                      <h4 class="text-success font-weight-bold mb-0">Rp {{ formatNumber(projectedRupiahCost) }}</h4>
                    </div>
                  </div>
                </div>

                <!-- Budget Guard Indicator -->
                <div class="p-3 rounded mb-3" style="background: rgba(16, 185, 129, 0.1); border: 1px solid rgba(16, 185, 129, 0.3);">
                  <div class="d-flex align-items-center gap-2 mb-1">
                    <i class="mdi mdi-shield-check text-success font-weight-bold" style="font-size: 20px;" aria-hidden="true"></i>
                    <span class="text-white font-weight-bold">{{ $t('studio.budgetSafety') }}</span>
                  </div>
                  <p class="text-muted small mb-0">{{ $t('studio.budgetSafe') }}</p>
                </div>
              </div>

              <div class="rounded mb-0 p-3 small text-muted" style="background: rgba(30, 41, 59, 0.4); border: 1px solid #27272a;">
                <i class="mdi mdi-lock-outline mr-1 text-info" aria-hidden="true"></i>
                {{ $t('studio.dailyCapNotice', { cap: formatNumber(companyDailyGoldCap) }) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- TAB 4: GEMINI AI ANTI-CHEAT VERIFICATION QUEUE -->
    <div v-if="activeTab === 'anticheat'">
      <div class="card bg-dark border-secondary mb-4">
        <div class="card-body p-3 p-md-4">
          <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div>
              <h4 class="card-title text-white mb-1">
                <i class="mdi mdi-shield-search text-success mr-1" aria-hidden="true"></i> {{ $t('studio.antiCheatTitle') }}
              </h4>
              <p class="text-muted small mb-0">
                {{ $t('studio.antiCheatDesc') }}
              </p>
            </div>
            <div class="d-flex gap-2">
              <button
                class="btn btn-success text-dark font-weight-bold btn-sm shadow-sm"
                @click="batchApproveVerified"
                :disabled="verifiedCount === 0"
              >
                <i class="mdi mdi-check-all mr-1" aria-hidden="true"></i> {{ $t('studio.batchApprove') }} ({{ verifiedCount }})
              </button>
              <button class="btn btn-outline-light btn-sm" :aria-label="$t('common.refresh')" @click="fetchQueue">
                <i class="mdi mdi-refresh" aria-hidden="true"></i>
              </button>
            </div>
          </div>

          <div v-if="pendingVerifications.length === 0" class="text-center py-5 text-muted">
            <i class="mdi mdi-check-circle-outline text-success" style="font-size: 54px;" aria-hidden="true"></i>
            <h5 class="text-white mt-2">{{ $t('studio.allCleanTitle') }}</h5>
            <p class="small">{{ $t('studio.allCleanSub') }}</p>
          </div>

          <div v-else class="table-responsive">
            <table class="table table-dark table-hover mb-0 align-middle" style="table-layout: fixed; width: 100%;">
              <thead>
                <tr class="text-muted small text-uppercase">
                  <th style="width: 150px;">{{ $t('studio.colEmployee') }}</th>
                  <th style="width: 170px;">{{ $t('studio.colQuestSop') }}</th>
                  <th style="width: 110px;">{{ $t('studio.colUploadedProof') }}</th>
                  <th style="width: 250px;">{{ $t('studio.colOcrVision') }}</th>
                  <th class="text-center" style="width: 110px;">{{ $t('studio.ocrConfidence') }}</th>
                  <th class="text-center" style="width: 140px;">{{ $t('studio.antiCheatChecks') }}</th>
                  <th class="text-right" style="width: 100px;">{{ $t('common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in pendingVerifications" :key="item.id">
                  <td>
                    <div class="font-weight-bold text-white">{{ item.rawUserName || $t('studio.defaultPlayerName') }}</div>
                    <small class="text-muted">{{ item.rawDivision || $t('studio.defaultDivisionName') }}</small>
                  </td>
                  <td>
                    <div class="text-white font-weight-bold">{{ item.rawQuestName || $t('studio.defaultQuestSubmission') }}</div>
                    <small class="text-warning">
                      <i class="mdi mdi-circle-multiple mr-1" aria-hidden="true"></i>+{{ item.gold }} {{ $t('common.gold') }} | +{{ item.xp }} XP
                    </small>
                  </td>
                  <td>
                    <div class="d-flex align-items-center gap-2">
                      <img
                        :src="item.evidenceThumbnail"
                        :alt="item.rawQuestName || $t('studio.colUploadedProof')"
                        class="rounded border border-secondary"
                        style="width: 44px; height: 44px; object-fit: cover; cursor: pointer;"
                        @click="zoomProof(item)"
                      />
                      <button class="btn btn-sm btn-link text-info p-0" @click="zoomProof(item)">{{ $t('studio.zoomBtn') }}</button>
                    </div>
                  </td>
                  <td>
                    <div v-if="item.ocrKeywords" class="small text-white mb-1">
                      <i class="mdi mdi-text-box-search-outline text-info mr-1" aria-hidden="true"></i>
                      <strong>{{ $t('studio.keywordsLabel') }}:</strong> {{ item.ocrKeywords }}
                    </div>
                    <div class="small text-muted" style="font-size: 11px;">
                      {{ item.rawAiSummary || $t('studio.awaitingManualReview') }}
                    </div>
                  </td>
                  <td class="text-center">
                    <span
                      v-if="item.confidence !== null && item.confidence !== undefined"
                      class="badge font-weight-bold px-2 py-1"
                      :class="item.confidence >= 85 ? 'badge-success text-dark' : (item.confidence >= 60 ? 'badge-warning text-dark' : 'badge-danger')"
                    >
                      {{ item.confidence }}% {{ $t('studio.matchSuffix') }}
                    </span>
                    <span v-else class="badge badge-secondary px-2 py-1">
                      {{ $t('studio.manualReviewBadge') }}
                    </span>
                  </td>
                  <td class="text-center small">
                    <div v-if="item.isHashUnique" class="text-success">
                      <i class="mdi mdi-fingerprint mr-1" aria-hidden="true"></i> {{ $t('studio.hashUnique') }}
                    </div>
                    <div v-else class="text-danger">
                      <i class="mdi mdi-alert-decagram mr-1" aria-hidden="true"></i> {{ $t('studio.hashDupe') }}
                    </div>
                    <div class="text-muted" style="font-size: 11px;">
                      <i class="mdi mdi-clock-check-outline mr-1" aria-hidden="true"></i>
                      {{ item.createdAt ? $t('studio.timestampPrefix', { time: item.createdAt }) : $t('studio.timestampValid') }}
                    </div>
                  </td>
                  <td class="text-right">
                    <div class="btn-group btn-group-sm">
                      <button
                        class="btn btn-success text-dark font-weight-bold"
                        @click="approveSingle(item)"
                        :title="$t('common.approve')"
                        :aria-label="$t('common.approve')"
                      >
                        <i class="mdi mdi-check" aria-hidden="true"></i>
                      </button>
                      <button
                        class="btn btn-outline-danger"
                        @click="rejectSingle(item)"
                        :title="$t('common.reject')"
                        :aria-label="$t('common.reject')"
                      >
                        <i class="mdi mdi-close" aria-hidden="true"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config/config';
import store from '@/store';
import Swal from 'sweetalert2';

const getDefaultStudioState = (locale) => {
  if (locale === 'en') {
    return {
      aiGoalPrompt: 'Close 2 Enterprise B2B client deals with signed DocuSign contracts',
      activeQuest: {
        name: 'Close 2 Enterprise B2B Clients & Signed Contract',
        division: 'Sales & Revenue',
        division_id: null,
        frequency: 'weekly',
        xp: 250,
        gold: 50,
        evidenceGuide: 'Upload a screenshot of the countersigned DocuSign agreement or closed-won CRM record link.',
        checklist: [
          'Verify MoU or commercial contract is signed by both parties',
          'Record the invoice value in the sales CRM pipeline',
          'Confirm the client onboarding schedule with Customer Success'
        ],
        hasConsequence: true,
      },
    };
  }
  return {
    aiGoalPrompt: 'Closing 2 klien B2B korporat beserta verifikasi kontrak resmi',
    activeQuest: {
      name: 'Closing 2 Klien B2B Korporat & Kontrak Resmi',
      division: 'Penjualan & Pendapatan',
      division_id: null,
      frequency: 'weekly',
      xp: 250,
      gold: 50,
      evidenceGuide: 'Unggah tangkapan layar kontrak resmi yang telah ditandatangani atau tautan penutupan transaksi pada CRM.',
      checklist: [
        'Memastikan dokumen MoU atau kontrak ditandatangani kedua belah pihak',
        'Mencatat nilai faktur ke sistem CRM penjualan',
        'Mengonfirmasi jadwal orientasi klien bersama tim layanan pelanggan'
      ],
      hasConsequence: true,
    },
  };
};

export default {
  name: 'MakerStudioView',
  data() {
    const initialLocale = localStorage.getItem('gamiteam_locale') || 'id';
    const initialDefaults = getDefaultStudioState(initialLocale);
    return {
      activeTab: 'architect',
      isGeneratingAi: false,
      isDeploying: false,
      hasEditedQuest: false,
      companyDailyGoldCap: 5000,
      aiGoalPrompt: initialDefaults.aiGoalPrompt,
      simTeamSize: 10,
      simCompletionRate: 85,
      divisions: [],
      questForm: {
        divisionId: null,
      },
      activeQuest: initialDefaults.activeQuest,
      pendingVerifications: [],
    };
  },
  computed: {
    localizedPresets() {
      if (this.$currentLocale === 'en') {
        return [
          {
            name: 'Enterprise B2B Deal Closure',
            division: 'Sales & Revenue',
            frequency: 'weekly',
            xp: 300,
            gold: 50,
            evidenceGuide: 'Countersigned contract document or verified client deposit receipt.',
            checklist: ['Verify contract legal review', 'Register client workspace', 'Confirm delivery milestones']
          },
          {
            name: 'Zero-Blocker Release QA & CI/CD Pass',
            division: 'Engineering & DevOps',
            frequency: 'daily',
            xp: 150,
            gold: 25,
            evidenceGuide: 'Merged pull request link passing automated test coverage above 85%.',
            checklist: ['All unit tests pass', 'Code review approved by 2 reviewers', 'Staging deployment verified']
          },
          {
            name: 'First Response Under 15 Minutes with Top CSAT',
            division: 'Customer Experience & Support',
            frequency: 'daily',
            xp: 100,
            gold: 20,
            evidenceGuide: 'Helpdesk ticket dashboard screenshot confirming 5-star customer rating.',
            checklist: ['Initial response sent within 15 minutes', 'Issue resolved clearly and politely', 'Customer satisfaction rating confirmed']
          },
          {
            name: 'On-Time Daily Standup & Sprint Update',
            division: 'Engineering & DevOps',
            frequency: 'daily',
            xp: 50,
            gold: 10,
            evidenceGuide: '09:00 standup attendance log and documented blocker notes.',
            checklist: ['Join standup before 09:00', 'Share completed progress from yesterday', 'Outline today execution plan']
          },
        ];
      }
      return [
        {
          name: 'Penutupan Kontrak Klien B2B Korporat',
          division: 'Penjualan & Pendapatan',
          frequency: 'weekly',
          xp: 300,
          gold: 50,
          evidenceGuide: 'Dokumen kontrak bertanda tangan sah atau bukti pembayaran uang muka klien.',
          checklist: ['Pemeriksaan legalitas kontrak selesai', 'Registrasi ruang kerja klien', 'Konfirmasi jadwal tahapan implementasi']
        },
        {
          name: 'Rilis Tanpa Kendala Kritis & Lulus Uji Otomatis',
          division: 'Rekayasa & DevOps',
          frequency: 'daily',
          xp: 150,
          gold: 25,
          evidenceGuide: 'Tautan pull request yang telah digabung dan lulus pengujian otomatis di atas 85%.',
          checklist: ['Seluruh pengujian unit lulus', 'Tinjauan kode disetujui oleh 2 peninjau', 'Penerapan ke lingkungan staging berhasil']
        },
        {
          name: 'Respon Awal di Bawah 15 Menit & Kepuasan Bintang 5',
          division: 'Layanan Pelanggan & Dukungan',
          frequency: 'daily',
          xp: 100,
          gold: 20,
          evidenceGuide: 'Tangkapan layar dasbor tiket bantuan dengan penilaian kepuasan bintang 5.',
          checklist: ['Respon awal dikirim di bawah 15 menit', 'Kendala diselesaikan secara tuntas dan sopan', 'Penilaian kepuasan pelanggan terkonfirmasi']
        },
        {
          name: 'Hadir Standup Harian Tepat Waktu & Pembaruan Sprint',
          division: 'Rekayasa & DevOps',
          frequency: 'daily',
          xp: 50,
          gold: 10,
          evidenceGuide: 'Catatan kehadiran rapat harian pukul 09.00 beserta dokumentasi hambatan kerja.',
          checklist: ['Hadir sebelum pukul 09.00', 'Menyampaikan pencapaian kerja hari sebelumnya', 'Menyampaikan rencana kerja hari ini']
        },
      ];
    },
    divisionOptions() {
      const defaults = this.$currentLocale === 'en'
        ? [
            'Sales & Revenue',
            'Engineering & DevOps',
            'Customer Experience & Support',
            'Retail & Operations',
            'All Employees',
          ]
        : [
            'Penjualan & Pendapatan',
            'Rekayasa & DevOps',
            'Layanan Pelanggan & Dukungan',
            'Ritel & Operasional',
            'Semua Karyawan',
          ];
      if (!this.divisions || this.divisions.length === 0) return defaults;
      const fromApi = this.divisions.map((d) => d.name).filter(Boolean);
      return Array.from(new Set([...fromApi, ...defaults]));
    },
    verifiedCount() {
      return this.pendingVerifications.filter(item => item.confidence !== null && item.confidence >= 85).length;
    },
    projectedMonthlyGold() {
      const questsPerMonth = this.activeQuest.frequency === 'daily' ? 22 : 4;
      return Math.round(this.simTeamSize * questsPerMonth * (this.simCompletionRate / 100) * this.activeQuest.gold);
    },
    projectedRupiahCost() {
      return this.projectedMonthlyGold * 1000;
    },
  },
  watch: {
    $currentLocale(newLocale) {
      if (!this.hasEditedQuest) {
        const nextDefaults = getDefaultStudioState(newLocale);
        this.aiGoalPrompt = nextDefaults.aiGoalPrompt;
        const prevDivId = this.activeQuest.division_id;
        this.activeQuest = {
          ...nextDefaults.activeQuest,
          division: this.divisions.length > 0 && prevDivId
            ? (this.divisions.find(d => d.id === prevDivId)?.name || nextDefaults.activeQuest.division)
            : nextDefaults.activeQuest.division,
          division_id: prevDivId,
        };
      }
    },
  },
  methods: {
    getHeaders() {
      const token = store.state.authToken || localStorage.getItem('authToken') || localStorage.getItem('token');
      return { Authorization: `Bearer ${token}` };
    },
    syncSelectedDivisionId() {
      const matched = (this.divisions || []).find(
        (d) => d && d.name === this.activeQuest.division
      );
      const id = matched ? matched.id : null;
      this.activeQuest.division_id = id;
      if (this.questForm) {
        this.questForm.divisionId = id;
      }
    },
    formatNumber(val) {
      if (!val) return '0';
      const localeTag = this.$currentLocale === 'id' ? 'id-ID' : 'en-US';
      return Math.round(val).toLocaleString(localeTag);
    },
    addChecklistItem() {
      this.hasEditedQuest = true;
      this.activeQuest.checklist.push('');
    },
    removeChecklistItem(idx) {
      this.hasEditedQuest = true;
      this.activeQuest.checklist.splice(idx, 1);
    },
    applyPreset(preset) {
      this.hasEditedQuest = true;
      this.activeQuest.name = preset.name;
      this.activeQuest.division = preset.division;
      this.syncSelectedDivisionId();
      this.activeQuest.frequency = preset.frequency;
      this.activeQuest.xp = preset.xp;
      this.activeQuest.gold = preset.gold;
      this.activeQuest.evidenceGuide = preset.evidenceGuide;
      this.activeQuest.checklist = [...preset.checklist];

      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: this.$t('studio.presetApplied'),
        showConfirmButton: false,
        timer: 1500
      });
    },
    async generateFormulaWithAi() {
      if (!this.aiGoalPrompt.trim()) {
        Swal.fire({
          icon: 'warning',
          title: this.$t('studio.goalEmptyTitle'),
          text: this.$t('studio.goalEmptyDesc'),
          confirmButtonColor: '#10b981'
        });
        return;
      }

      this.isGeneratingAi = true;
      this.hasEditedQuest = true;
      try {
        const res = await axios.post(`${config.API_URL}ai/generate-rules`, {
          goal: this.aiGoalPrompt,
          prompt: this.aiGoalPrompt,
          division: this.activeQuest.division,
          locale: this.$currentLocale
        }, {
          headers: this.getHeaders()
        });

        const rules = res.data?.data?.rules || res.data?.data?.quests || [];
        if (Array.isArray(rules) && rules.length > 0) {
          const rule = rules[0];
          this.activeQuest.name = rule.name;
          this.activeQuest.gold = rule.gold || 50;
          this.activeQuest.xp = (rule.gold || 50) * 3;
          this.activeQuest.evidenceGuide = rule.description;
          this.activeQuest.frequency = rule.type === 'daily' ? 'daily' : 'weekly';
          if (rule.checklist && Array.isArray(rule.checklist)) {
            this.activeQuest.checklist = rule.checklist;
          }
        }
      } catch (err) {
        const p = this.aiGoalPrompt.trim();
        this.activeQuest.name = this.$t('studio.aiFallbackName', { goal: p.substring(0, 45) });
        this.activeQuest.gold = 35;
        this.activeQuest.xp = 150;
        this.activeQuest.evidenceGuide = this.$t('studio.aiFallbackEvidence', { goal: p });
        this.activeQuest.checklist = [
          this.$t('studio.aiFallbackCheck1'),
          this.$t('studio.aiFallbackCheck2'),
          this.$t('studio.aiFallbackCheck3'),
        ];
      } finally {
        this.isGeneratingAi = false;
        Swal.fire({
          icon: 'success',
          title: this.$t('studio.formulaGeneratedTitle'),
          text: this.$t('studio.formulaGeneratedDesc'),
          confirmButtonColor: '#10b981'
        });
      }
    },
    async deployActiveQuest() {
      return this.deployQuestToRealm();
    },
    async deployQuestToRealm() {
      if (!this.activeQuest.name) {
        Swal.fire({
          icon: 'warning',
          title: this.$t('studio.questNameRequired'),
          confirmButtonColor: '#10b981'
        });
        return;
      }

      this.isDeploying = true;
      try {
        const freq = this.activeQuest.frequency || 'daily';
        let repeat_type = 'no_repeat';
        let repeat_on_daily = null;
        let repeat_on_day = null;
        let repeat_on_date = null;

        if (freq === 'daily') {
          repeat_type = 'daily';
          repeat_on_daily = {
            monday: true,
            tuesday: true,
            wednesday: true,
            thursday: true,
            friday: true,
            saturday: true,
            sunday: true
          };
        } else if (freq === 'weekly') {
          repeat_type = 'weekly';
          repeat_on_day = 1;
        } else if (freq === 'quarterly') {
          repeat_type = 'monthly';
          repeat_on_date = 1;
        }

        this.syncSelectedDivisionId();
        const assignedDivisionId =
          this.questForm?.divisionId || this.activeQuest.division_id || null;

        // Create matching consequence FIRST so consequence_id is properly linked on the Rule
        let consequenceId = null;
        if (this.activeQuest.hasConsequence) {
          try {
            const cRes = await axios.post(`${config.API_URL}consequence`, {
              name: this.$t('studio.consequenceAutoName', { name: this.activeQuest.name }),
              gold: Math.max(5, Math.round((Number(this.activeQuest.gold) || 25) * 0.5)),
              description: this.$t('studio.consequenceAutoDesc', { name: this.activeQuest.name }),
            }, {
              headers: this.getHeaders()
            });
            consequenceId = cRes.data?.data?.id || cRes.data?.id || null;
          } catch (cErr) {
            consequenceId = null;
          }
        }

        const payload = {
          name: this.activeQuest.name,
          gold: Number(this.activeQuest.gold) || 25,
          xp_reward: Number(this.activeQuest.xp) || 50,
          type: freq === 'daily' ? 'always_active' : 'onetime',
          repeat_type,
          repeat_on_daily,
          repeat_on_day,
          repeat_on_date,
          assigned_division_id: assignedDivisionId,
          consequence_id: consequenceId,
          description: this.activeQuest.evidenceGuide || this.$t('studio.defaultQuestDescription'),
          checklist: this.activeQuest.checklist || [],
          status: 'published',
          start_date: new Date().toISOString().split('T')[0]
        };

        await axios.post(`${config.API_URL}rule`, payload, {
          headers: this.getHeaders()
        });

        Swal.fire({
          icon: 'success',
          title: this.$t('studio.questDeployedTitle'),
          text: this.$t('studio.questDeployedDesc', { name: this.activeQuest.name || '' }),
          confirmButtonColor: '#10b981'
        });
      } catch (err) {
        const errMsg = err.response?.data?.message || (err.response?.data?.errors ? Object.values(err.response.data.errors).flat().join(', ') : err.message);
        Swal.fire({
          icon: 'error',
          title: this.$t('studio.questDeployFailed'),
          text: errMsg || this.$t('studio.questDeployErrorDesc'),
          confirmButtonColor: '#ef4444'
        });
      } finally {
        this.isDeploying = false;
      }
    },
    zoomProof(item) {
      if (item && item.evidenceThumbnail) {
        const qName = item.rawQuestName || this.$t('studio.sopEvidenceModalTitle');
        const uName = item.rawUserName || this.$t('studio.defaultPlayerName');
        const divName = item.rawDivision || this.$t('studio.defaultDivisionName');
        Swal.fire({
          title: qName,
          html: `<div class="text-muted small mb-2"><i class="mdi mdi-account mr-1"></i>${uName} (${divName})</div>`,
          imageUrl: item.evidenceThumbnail,
          imageAlt: qName,
          imageWidth: 500,
          showCloseButton: true,
          confirmButtonText: this.$t('studio.closePreview'),
          confirmButtonColor: '#10b981',
          background: '#13192b',
          color: '#ffffff'
        });
      }
    },
    async approveSingle(item) {
      try {
        await axios.post(`${config.API_URL}v1/maker/approvals/bulk-action`, {
          type: 'rules',
          action: 'approve',
          ids: [item.id],
        }, { headers: this.getHeaders() });
        this.pendingVerifications = this.pendingVerifications.filter(p => p.id !== item.id);
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: this.$t('studio.proofApprovedToast', {
            name: item.rawQuestName || this.$t('studio.defaultQuestSubmission'),
            gold: item.gold || 0
          }),
          showConfirmButton: false,
          timer: 2000
        });
      } catch (err) {
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'error',
          title: err.response?.data?.message || this.$t('studio.approveProofError'),
          showConfirmButton: false,
          timer: 2500
        });
      }
    },
    async rejectSingle(item) {
      const { value: reason } = await Swal.fire({
        title: this.$t('studio.rejectProofTitle'),
        input: 'text',
        inputLabel: this.$t('studio.rejectReasonLabel'),
        inputValue: this.$t('studio.rejectReasonDefault'),
        showCancelButton: true,
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#4b5563',
        confirmButtonText: this.$t('studio.rejectProofBtn'),
        cancelButtonText: this.$t('common.cancel'),
      });

      if (reason) {
        try {
          await axios.post(`${config.API_URL}v1/maker/approvals/bulk-action`, {
            type: 'rules',
            action: 'reject',
            ids: [item.id],
            comment: reason,
          }, { headers: this.getHeaders() });
          this.pendingVerifications = this.pendingVerifications.filter(p => p.id !== item.id);
          Swal.fire({
            icon: 'info',
            title: this.$t('studio.proofRejectedTitle'),
            text: this.$t('studio.proofRejectedDesc', { reason }),
            confirmButtonColor: '#10b981'
          });
        } catch (err) {
          Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'error',
            title: err.response?.data?.message || this.$t('studio.rejectProofError'),
            showConfirmButton: false,
            timer: 2500
          });
        }
      }
    },
    async batchApproveVerified() {
      const count = this.verifiedCount;
      const idsToApprove = this.pendingVerifications.filter(item => item.confidence !== null && item.confidence >= 85).map(item => item.id);
      if (idsToApprove.length > 0) {
        try {
          await axios.post(`${config.API_URL}v1/maker/approvals/bulk-action`, {
            type: 'rules',
            action: 'approve',
            ids: idsToApprove,
          }, { headers: this.getHeaders() });
        } catch (err) {
          Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'error',
            title: err.response?.data?.message || this.$t('studio.approveProofError'),
            showConfirmButton: false,
            timer: 2500
          });
          return;
        }
      }
      this.pendingVerifications = this.pendingVerifications.filter(item => item.confidence === null || item.confidence < 85);
      Swal.fire({
        icon: 'success',
        title: this.$t('studio.batchApproveSuccess'),
        text: this.$t('studio.batchApproveSuccessDesc', { count }),
        confirmButtonColor: '#10b981'
      });
    },
    async fetchCompanyGameSettings() {
      try {
        const res = await axios.get(`${config.API_URL}game-settings`, {
          headers: this.getHeaders(),
        });
        const settings = res.data?.data || res.data || {};
        if (Number(settings.daily_gold_cap) > 0) {
          this.companyDailyGoldCap = Number(settings.daily_gold_cap);
        }
      } catch (err) {
        this.companyDailyGoldCap = 5000;
      }
    },
    async fetchDivisions() {
      try {
        const res = await axios.get(`${config.API_URL}division`, {
          headers: this.getHeaders(),
        });
        const list = Array.isArray(res.data?.data) ? res.data.data : (Array.isArray(res.data) ? res.data : []);
        if (list.length > 0) {
          this.divisions = list;
          if (!this.hasEditedQuest && list[0]?.name) {
            this.activeQuest.division = list[0].name;
          }
          this.syncSelectedDivisionId();
        }
      } catch (err) {
        // Fallback to localized default divisions
      }
    },
    async fetchQueue() {
      try {
        const res = await axios.get(`${config.API_URL}v1/maker/approvals/rewards`, {
          headers: this.getHeaders(),
        });
        const rawList = Array.isArray(res.data?.data?.data)
          ? res.data.data.data
          : Array.isArray(res.data?.data)
          ? res.data.data
          : null;
        if (Array.isArray(rawList)) {
          this.pendingVerifications = rawList.map(item => ({
            id: item.id,
            rawUserName: item.user?.name || null,
            rawDivision: item.user?.division?.name || null,
            rawQuestName: item.rule?.name || null,
            userName: item.user?.name || '',
            division: item.user?.division?.name || '',
            questName: item.rule?.name || '',
            gold: item.gold || item.rule?.gold || 10,
            xp: item.rule?.xp_reward || 50,
            evidenceThumbnail: item.evidence ? (config.STORAGE_URL + item.evidence) : '',
            ocrKeywords: item.ocr_keywords || item.ai_keywords || null,
            aiStatus: item.ai_status || item.ocr_keywords || null,
            rawAiSummary: item.ai_summary || item.comment || null,
            confidence: item.ai_confidence ?? item.confidence ?? null,
            aiConfidence: item.ai_confidence ?? item.confidence ?? null,
            isHashUnique: item.is_hash_unique ?? true,
            createdAt: item.created_at || null,
            raw: item
          }));
        }
      } catch (err) {
        this.pendingVerifications = [];
      }
    }
  },
  mounted() {
    this.fetchCompanyGameSettings();
    this.fetchDivisions();
    this.fetchQueue();
  }
};
</script>

<style scoped>
.maker-studio-view .card {
  border-radius: 12px;
}

/* Smartphone Mockup Frame */
.phone-frame-mockup {
  width: 330px;
  min-height: 520px;
  background: #000;
  border-radius: 36px;
  border: 10px solid #1f293d;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.7), 0 0 30px rgba(16, 185, 129, 0.15);
  overflow: hidden;
  position: relative;
}

.phone-notch {
  width: 130px;
  height: 20px;
  background: #1f293d;
  margin: 0 auto;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.notch-speaker {
  width: 36px;
  height: 4px;
  background: #334155;
  border-radius: 4px;
}

.notch-lens {
  width: 6px;
  height: 6px;
  background: #0f172a;
  border-radius: 50%;
  border: 1px solid #334155;
}

.phone-screen-content {
  background: #0b0f19;
  min-height: 490px;
  color: #fff;
}

/* Table styling within design system bounds */
.maker-studio-view .table-responsive {
  border-radius: 8px;
}

.maker-studio-view .table-dark {
  background-color: transparent;
}

.maker-studio-view .table-dark td,
.maker-studio-view .table-dark th {
  border-color: rgba(255, 255, 255, 0.08);
  vertical-align: middle;
}
</style>
